<template>
	<div
		class="lp-flip-card-view"
		:class="{
			animation: mode !== 'edit',
			hover: value.data.trigger === 'hover',
			click: value.data.trigger === 'click' && flip,
			pointer: value.data.trigger === 'click',
			'lp-flip-card-view-edit': mode === 'edit'
		}"
		@click.stop="handleClickEvent"
		@mouseover.stop="handleHoverEvent"
	>
		<div class="lp-flip-card-view-content">
			<template>
				<flip-card-side
					class="front"
					:class="{ hidden: side === 'back' }"
					:style="styleFront"
					:blockVariables="blockVariables"
					:blocks="sideBlocks('front')"
					:parentBlock="value"
					:mode="mode"
					side="front"
					:icon="value.data.front.icon"
				/>
				<flip-card-side
					class="back"
					:class="{ hidden: side === 'front' }"
					:style="styleBack"
					:blockVariables="blockVariables"
					:blocks="sideBlocks('back')"
					:parentBlock="value"
					side="back"
					:mode="mode"
					:icon="value.data.back.icon"
				/>
			</template>
		</div>
	</div>
</template>

<script>
import { RevisionDisplayMode } from 'plant-common/src/models/revisionDisplayMode.js';
import FlipCardSide from './FlipCardSide.vue';
import { FlipCardSide as FlipCardSideModel } from './flipCardSide.js';

const NonInteractiveBlockTypes = new Set([
	'image',
	'shape',
	'shapeArrow',
	'shapeArrow90',
	'shapeArrow180',
	'shapeBrace',
	'shapeCallout',
	'shapeDiamond',
	'shapeDoubleArrow',
	'shapeLine',
	'shapeLineConnector',
	'shapeOval',
	'shapeRectangle',
	'shapeTriangle',
	'table',
	'textarea'
]);

export default {
	components: { FlipCardSide },
	props: ['mode', 'value', 'side', 'blockVariables', 'editable'],
	data() {
		return {
			flip: false,
			oldBlocks: {
				front: [],
				back: []
			}
		};
	},
	mounted() {
		const frontBlocks = this.sideBlocks(FlipCardSideModel.front);
		const backBlocks = this.sideBlocks(FlipCardSideModel.back);

		this.oldBlocks.front = structuredClone(frontBlocks);
		this.oldBlocks.back = structuredClone(backBlocks);

		if (this.mode === RevisionDisplayMode.edit) return;

		const allSideBlocks = [...frontBlocks, ...backBlocks];

		this.setBlocksStyle(allSideBlocks);
	},
	computed: {
		styleFront() {
			return this.getStyle(FlipCardSideModel.front);
		},
		styleBack() {
			return this.getStyle(FlipCardSideModel.back);
		}
	},
	methods: {
		getStyle(side) {
			return {
				backgroundColor: this.value.data[side].backgroundColor,
				borderWidth: this.value.data[side].border.widthInPx + 'px',
				borderColor: this.value.data[side].border.color,
				borderStyle: this.value.data[side].border.style
			};
		},
		setAsVisited(action) {
			if (!this.value.data.visited && action === this.value.data.trigger && this.mode !== 'edit') {
				this.$set(this.value.data, 'visited', true);
			}
		},
		sideBlocks(side) {
			return this.value.data[side].blocks || [];
		},
		handleClickEvent(event) {
			const eventTarget = event.target;
			const targetClassList = eventTarget.classList;
			const isFlipCardElement = targetClassList.contains('flip-card-side');
			const isFlipCardIconElement = targetClassList.contains('flip-card-side__icon');

			if (!isFlipCardElement && !isFlipCardIconElement) return;

			this.flip = !this.flip;
			this.setAsVisited('click');
		},
		handleHoverEvent() {
			this.setAsVisited('hover');
		},
		setBlocksStyle(blocks) {
			const interactiveBlockIds = this.value.data.interactiveBlockIds;

			blocks.forEach(block => {
				if (interactiveBlockIds.includes(block.id)) return;

				const element = document.getElementById(block.id);
				if (element) element.style.pointerEvents = 'none';
			});
		},
		handleBlocksLengthChange(side, newLength, oldLength) {
			if (this.mode !== RevisionDisplayMode.edit) return;

			const newBlocks = structuredClone(this.value.data[side].blocks);

			if (newLength > oldLength) {
				this.addBlockToInteractiveBlocksList(side, newBlocks);
			} else if (newLength < oldLength) {
				this.removeBlockFromInteractiveBlocksList(side, newBlocks);
			}

			this.oldBlocks[side] = newBlocks;
		},
		addBlockToInteractiveBlocksList(side, newBlocks) {
			const addedBlock = newBlocks.find(newBlock => !this.oldBlocks[side].some(oldBlock => oldBlock.id === newBlock.id));

			if (!addedBlock || !this.isInteractiveBlock(addedBlock)) return;

			this.value.data.interactiveBlockIds.push(addedBlock.id);
		},
		isInteractiveBlock(block) {
			const isInteractiveBlock = !NonInteractiveBlockTypes.has(block.type);
			return isInteractiveBlock;
		},
		removeBlockFromInteractiveBlocksList(side, newBlocks) {
			const deletedBlock = this.oldBlocks[side].find(oldBlock => !newBlocks.some(newBlock => newBlock.id === oldBlock.id));

			if (!deletedBlock) return;

			this.value.data.interactiveBlockIds = this.value.data.interactiveBlockIds.filter(
				id => id !== deletedBlock.id
			);
		}
	},
	watch: {
		'value.data.front.blocks.length'(newLength, oldLength) {
			this.handleBlocksLengthChange(FlipCardSideModel.front, newLength, oldLength);
		},
		'value.data.back.blocks.length'(newLength, oldLength) {
			this.handleBlocksLengthChange(FlipCardSideModel.back, newLength, oldLength);
		}
	}
};
</script>

<style>
/* Default style */
.lp-flip-card-view {
	width: 100%;
	height: 100%;
}

.lp-flip-card-view-content {
	width: 100%;
	height: 100%;
	/* user-select: none; */
}
.lp-flip-card-view-content .front,
.lp-flip-card-view-content .back {
	width: 100%;
	height: 100%;
	/* display: flex; */
}
.lp-flip-card-view:not(.animation) .hidden {
	display: none;
}

/* Animation in viewer mode */
.lp-flip-card-view.animation {
	position: absolute;
	width: 100%;
}
.lp-flip-card-view.animation .lp-flip-card-view-content {
	position: absolute;
	transition: transform 1s;
	transform-style: preserve-3d;
}

/* On hover */
.lp-flip-card-view.animation.hover:hover .lp-flip-card-view-content {
	transform: rotateY(180deg);
	transition: transform 1s;
}
/* On click */
.lp-flip-card-view.animation.click .lp-flip-card-view-content {
	transform: rotateY(180deg);
	transition: transform 1s;
}
.lp-flip-card-view.animation.pointer {
	cursor: pointer;
}
.lp-flip-card-view.animation .front,
.lp-flip-card-view.animation .back {
	position: absolute;
	height: 100%;
	width: 100%;
}
.lp-flip-card-view.animation .front,
.lp-flip-card-view.animation .front > div,
.lp-flip-card-view.animation .back,
.lp-flip-card-view.animation .back > div {
	backface-visibility: hidden;
}

.lp-flip-card-view.animation .back {
	transform: rotateY(180deg);
}

.lp-flip-card-view-edit .fields:hover {
	background: rgb(235, 235, 235);
	padding: 2px;
	display: inline-block;
}
/* Tooltip */
.lp-flip-card-view-edit .fields:before {
	content: attr(data-field);
	position: absolute;
	bottom: 100%;
	transform: translateX(50%);
	right: 50%;
	padding: 10px;
	width: 100%;
	min-width: 300px;
	background-color: #ffffff;
	text-align: left;
	font-size: 30px;
	display: none;
}
.lp-flip-card-view-edit .fields:hover:before {
	display: block;
}
</style>
